// src/utils/emailTemplates.js
// ============================================================================
// EMAIL TEMPLATE SYSTEM
// ============================================================================
// Config-driven templates with dynamic placeholder rendering + mailto URL builder.
//
// TEMPLATE CATEGORIES:
//   Stage Initiation, Timeline Change, Stage Closure,
//   Tax Invoice, Late Payment Reminder, Monthly Accountant Report
//
// PLACEHOLDERS (auto-populated from project/client/invoice context):
//   {addressee}       — Required. The person's name in the greeting.
//   {clientName}      — Client organisation name
//   {projectName}     — Project display name
//   {projectCode}     — Project code (e.g. "ARC-2026-001")
//   {stageName}       — Current stage name
//   {nextStage}       — Next stage name (auto-derived)
//   {previousStage}   — Previous stage name (auto-derived)
//   {invoiceNumber}   — Invoice number
//   {invoiceDate}     — Invoice date (formatted DD/MM/YYYY)
//   {dueDate}         — Payment due date (formatted DD/MM/YYYY)
//   {paymentTermsDays} — Client payment terms in days
//   {amount}          — Invoice amount
//   {meetingDate}     — Meeting/event date (formatted DD/MM/YYYY)
//   {teamList}        — Formatted team member list
//   {timeline}        — Formatted timeline block
//   {date}            — Generic date (formatted DD/MM/YYYY)
//
// All generated templates remain FULLY EDITABLE in the EmailDraftModal.
// ============================================================================

// ── Admin email for BCC on finance communications ───────────────────────────
const ADMIN_EMAIL = 'admin@architecturebrio.com';

// ── Stage abbreviation → full name mapping ──────────────────────────────────
// Used in email subjects/bodies so clients see descriptive stage names.
const STAGE_FULL_NAMES = {
  'Advance':    'Advance',
  'PCD':        'PCD (Masterplan/Pre Concept)',
  'CD AD':      'CD AD (Concept Design - Architecture)',
  'CD ID':      'CD ID (Concept Design - Interior)',
  'SD AD':      'SD AD (Schematic Design - Architecture)',
  'SD ID':      'SD ID (Schematic Design - Interior)',
  'DD AD':      'DD AD (Design Development - Architecture)',
  'DD ID':      'DD ID (Design Development - Interior)',
  'AoC AD':     'AoC AD (Awarding of Contract - Architecture)',
  'AoC ID':     'AoC ID (Awarding of Contract - Interior)',
  'TD AD':      'TD AD (Tender Design - Architecture)',
  'TD ID':      'TD ID (Tender Design - Interior)',
  'GFC 1':      'GFC 1 (Good for Construction 1)',
  'GFC 2':      'GFC 2 (Good for Construction 2)',
  'GFC 3':      'GFC 3 (Good for Construction 3)',
  'Completion': 'Completion',
  'CC':         'CC (Construction Coordination)',
};

/**
 * Expand a stage abbreviation to its full name for use in emails.
 * If the name is already the full form or not recognized, returns it as-is.
 */
function expandStageName(name) {
  if (!name) return '';
  return STAGE_FULL_NAMES[name] || name;
}

/**
 * Extract the abbreviation from a stage name.
 * Handles both forms: "CD AD" (already abbreviated) and "PCD (Masterplan/Pre Concept)" → "PCD".
 * Returns the part before any parenthetical, trimmed.
 */
export function getStageAbbreviation(name) {
  if (!name) return '';
  const parenIdx = name.indexOf('(');
  if (parenIdx > 0) return name.slice(0, parenIdx).trim();
  return name;
}

/**
 * Compact display label for a part (sub) stage. Auto-generated part names
 * follow "{parent} — Part {n}"; render them as "{parent w/o trailing digits}-Part {n}"
 * (e.g. "GFC1 — Part 7" → "GFC-Part 7", "CD AD — Part 2" → "CD AD-Part 2") so they
 * fit the narrow stage column. Non-part names fall through to getStageAbbreviation.
 */
export function getPartStageAbbreviation(name) {
  if (!name) return '';
  const m = name.match(/^(.*?)\s*[—–]\s*Part\b\s*(.*)$/);
  if (!m) return getStageAbbreviation(name);
  const parent = getStageAbbreviation(m[1]).replace(/\d+$/, '').trim();
  const suffix = m[2].trim();
  return suffix ? `${parent}-Part ${suffix}` : `${parent}-Part`;
}

// ── All supported placeholders ──────────────────────────────────────────────
const ALL_PLACEHOLDERS = [
  'addressee', 'clientName', 'projectName', 'projectCode',
  'stageName', 'nextStage', 'previousStage',
  'invoiceNumber', 'invoiceDate', 'dueDate', 'paymentTermsDays', 'amount',
  'meetingDate', 'teamList', 'timeline', 'date',
  'previousDate',
  'candidateName', 'jobRole', 'officeLocation',
];

// ── Template categories ─────────────────────────────────────────────────────
const TEMPLATE_CATEGORIES = [
  { key: 'stage_initiation', label: 'Stage Initiation', isFinance: false },
  { key: 'timeline_change', label: 'Timeline Change', isFinance: false },
  { key: 'stage_closure', label: 'Stage Closure', isFinance: false },
  { key: 'payment_confirmed', label: 'Payment Confirmed', isFinance: false },
  { key: 'tax_invoice', label: 'Tax Invoice', isFinance: true },
  { key: 'proforma_invoice', label: 'Proforma Invoice', isFinance: true },
  { key: 'late_payment_reminder', label: 'Late Payment Reminder', isFinance: true },
  { key: 'monthly_accountant_report', label: 'Monthly Accountant Report', isFinance: true },
  { key: 'hiring', label: 'Hiring', isFinance: false, isHiring: true },
];

// ── Template definitions ────────────────────────────────────────────────────
const TEMPLATES = {
  // ── Stage Initiation ────────────────────────────────────────────────────
  project_initiated: {
    subject: '{projectName} — Project Initiated',
    body: [
      'Dear {addressee},',
      '',
      'We are delighted to confirm that the project {projectName} ({projectCode}) for {clientName} has been officially initiated as of {date}.',
      '',
      'The first stage, {stageName}, will commence shortly. Below is the planned timeline:',
      '',
      '{timeline}',
      '',
      'Our team members involved in this project:',
      '{teamList}',
      '',
      'We look forward to a successful collaboration. Please do not hesitate to reach out if you have any questions.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_initiation',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  stage_initiated: {
    subject: '{projectName} — {stageName} initiated',
    body: [
      'Dear {addressee},',
      '',
      'We are pleased to inform you that the {stageName} stage for project {projectName} ({projectCode}) has been initiated as of {date}.',
      '',
      'We will keep you updated on the progress. Please do not hesitate to reach out if you have any questions.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_initiation',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Stage Completed (actual date set → proforma invoice trigger) ──
  stage_completed: {
    subject: '{projectName} — {stageName} completed',
    body: [
      'Dear {addressee},',
      '',
      'We are pleased to inform you that the {stageName} stage for project {projectName} ({projectCode}) has been completed on {date}.',
      '',
      'Please find attached the proforma invoice for this stage. We kindly request your review at your earliest convenience.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_closure',
    isFinance: true,
    requiresInvoicePdf: true,
  },
  // ── Stage Approved (kept for backwards compat, category: stage_closure) ──
  stage_approved: {
    subject: '{projectName} — {stageName} approved',
    body: [
      'Dear {addressee},',
      '',
      'Thank you for approving the {stageName} stage for project {projectName} ({projectCode}) on {date}.',
      '',
      'We will proceed with the next steps as discussed. Please feel free to reach out if you need anything.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_closure',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Timeline Change ─────────────────────────────────────────────────────
  timeline_change: {
    subject: '{projectName} — Timeline Update for {stageName}',
    body: [
      'Dear {addressee},',
      '',
      'We would like to inform you of an update to the timeline for project {projectName} ({projectCode}).',
      '',
      'Stage: {stageName}',
      'Updated date: {date}',
      '',
      '{timeline}',
      '',
      'Please do not hesitate to reach out if you have any questions regarding this update.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'timeline_change',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Planned Date Revised (native mail — project-stage) ─────────────────
  // Triggered when planned date changes by more than threshold days.
  // Subject follows PROJECTCODE_Stage format.
  planned_date_revised: {
    subject: '{projectCode}_Stage Planned Date Revised',
    body: [
      'Dear {addressee},',
      '',
      'We would like to inform you of a revision to the planned timeline for project {projectName} ({projectCode}).',
      '',
      'Stage: {stageName}',
      'Previous planned date: {previousDate}',
      'Revised planned date: {date}',
      '',
      'We apologise for any inconvenience this may cause and will continue to keep you informed of progress.',
      '',
      '{timeline}',
      '',
      'Please do not hesitate to reach out if you have any questions.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'timeline_change',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Stage Completion Approval Request (native mail — project-stage) ────
  // Triggered on Actual Date: confirms stage scope completed, requests approval.
  stage_completion_approval: {
    subject: '{projectCode}_Stage Completion Approval Request',
    body: [
      'Dear {addressee},',
      '',
      'We are pleased to inform you that the {stageName} stage for project {projectName} ({projectCode}) has been completed as of {date}.',
      '',
      'The scope of work completed in this stage includes:',
      '• {stageName} deliverables as per the agreement',
      '',
      'We kindly request your review and approval of the completed stage at your earliest convenience.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_closure',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Payment Confirmed / Next Stage (native mail — project-stage) ───────
  // Triggered on Paid Date: confirms payment received, announces next stage.
  payment_confirmed: {
    subject: '{projectCode}_Stage Payment Confirmed',
    body: [
      'Dear {addressee},',
      '',
      'We are pleased to confirm that payment for the {stageName} stage of project {projectName} ({projectCode}) has been received and confirmed.',
      '',
      'The next stage, {nextStage}, will commence as per the agreed timeline.',
      '',
      '{timeline}',
      '',
      'Thank you for your continued trust. Please do not hesitate to reach out if you have any questions.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'payment_confirmed',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Proforma Invoice (admin/RADAR — invoice communication) ─────────────
  // Triggered via RADAR on Proforma Invoice Date. Admin-facing action item.
  proforma_invoice_admin: {
    subject: '{projectCode}_Invoice Proforma Invoice',
    body: [
      'Action required: Send proforma invoice for the {stageName} stage of project {projectName} ({projectCode}).',
      '',
      'Client: {clientName}',
      'Stage: {stageName}',
      'Proforma Invoice Date: {date}',
      '',
      'Please prepare and send the proforma invoice to the client.',
      'If relevant, also share the invoicing ledger.',
    ].join('\n'),
    category: 'proforma_invoice',
    isFinance: true,
    requiresInvoicePdf: true,
  },
  // ── Tax Invoice (admin/RADAR — invoice communication) ──────────────────
  // Triggered via RADAR on Invoice Date. Admin-facing action item.
  tax_invoice_admin: {
    subject: '{projectCode}_Invoice Tax Invoice',
    body: [
      'Action required: Send tax invoice for the {stageName} stage of project {projectName} ({projectCode}).',
      '',
      'Client: {clientName}',
      'Stage: {stageName}',
      'Invoice Date: {invoiceDate}',
      '',
      'Please prepare and send the tax invoice to the client.',
    ].join('\n'),
    category: 'tax_invoice',
    isFinance: true,
    requiresInvoicePdf: true,
  },
  // ── Stage Closure ───────────────────────────────────────────────────────
  stage_closure: {
    subject: '{projectName} — {stageName} completed',
    body: [
      'Dear {addressee},',
      '',
      'We are pleased to inform you that the {stageName} stage for project {projectName} ({projectCode}) has been completed as of {date}.',
      '',
      'The next stage, {nextStage}, will commence as per the agreed timeline.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'stage_closure',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Tax Invoice ─────────────────────────────────────────────────────────
  invoice_sent: {
    subject: '{projectName} — Invoice for {stageName}',
    body: [
      'Dear {addressee},',
      '',
      'Please find attached the invoice for the {stageName} stage of project {projectName} ({projectCode}), dated {invoiceDate}.',
      '',
      'Invoice number: {invoiceNumber}',
      'Amount: {amount}',
      'Payment due: {dueDate} ({paymentTermsDays} days)',
      '',
      'If you have any questions regarding this invoice, please do not hesitate to contact us.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'tax_invoice',
    isFinance: true,
    requiresInvoicePdf: true,
  },
  // ── Late Payment Reminders ──────────────────────────────────────────────
  reminder_friendly: {
    subject: '{projectName} — Friendly Reminder: Invoice {invoiceNumber}',
    body: [
      'Dear {addressee},',
      '',
      'This is a gentle reminder regarding invoice {invoiceNumber} for project {projectName}, dated {invoiceDate}, with a total amount of {amount}.',
      '',
      'The payment was due on {dueDate}. If you have already processed the payment, please disregard this message.',
      '',
      'If you have any questions, please feel free to reach out.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'late_payment_reminder',
    isFinance: true,
    requiresInvoicePdf: false,
  },
  reminder_due_today: {
    subject: '{projectName} — Payment Due Today: Invoice {invoiceNumber}',
    body: [
      'Dear {addressee},',
      '',
      'This is a reminder that payment for invoice {invoiceNumber} for project {projectName} is due today ({dueDate}).',
      '',
      'Invoice date: {invoiceDate}',
      'Amount due: {amount}',
      '',
      'We would appreciate your prompt attention to this matter.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'late_payment_reminder',
    isFinance: true,
    requiresInvoicePdf: false,
  },
  reminder_overdue: {
    subject: '{projectName} — Overdue: Invoice {invoiceNumber}',
    body: [
      'Dear {addressee},',
      '',
      'We would like to bring to your attention that invoice {invoiceNumber} for project {projectName}, dated {invoiceDate}, remains unpaid.',
      '',
      'The payment of {amount} was due on {dueDate}.',
      '',
      'We kindly request you to arrange the payment at the earliest. If the payment has already been made, please share the details so we can update our records.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'late_payment_reminder',
    isFinance: true,
    requiresInvoicePdf: false,
  },
  reminder_final: {
    subject: '{projectName} — Final Reminder: Invoice {invoiceNumber}',
    body: [
      'Dear {addressee},',
      '',
      'This is a final reminder regarding the overdue invoice {invoiceNumber} for project {projectName}.',
      '',
      'Invoice date: {invoiceDate}',
      'Amount due: {amount}',
      'Due date: {dueDate}',
      '',
      'Despite our earlier reminders, we have not yet received the payment. We urge you to arrange the payment immediately to avoid any disruption to ongoing services.',
      '',
      'Please contact us if there are any concerns.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'late_payment_reminder',
    isFinance: true,
    requiresInvoicePdf: false,
  },
  // ── Monthly Accountant Report ───────────────────────────────────────────
  monthly_accountant_report: {
    subject: '{projectName} — Monthly Invoice Summary',
    body: [
      'Dear {addressee},',
      '',
      'Please find below the monthly invoice summary for project {projectName} ({projectCode}).',
      '',
      '{timeline}',
      '',
      'Team members involved:',
      '{teamList}',
      '',
      'Please let us know if you require any additional information.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'monthly_accountant_report',
    isFinance: true,
    requiresInvoicePdf: false,
  },
  // ── Hiring Templates ──────────────────────────────────────────────────────
  hiring_offer: {
    subject: 'Architecture BRIO — Offer of Position: {jobRole}',
    body: [
      'Dear {candidateName},',
      '',
      'We are pleased to extend an offer of employment for the position of {jobRole} at Architecture BRIO, {officeLocation} office.',
      '',
      'We were very impressed with your profile and believe you would be a valuable addition to our team.',
      '',
      'Please find enclosed the details of this offer. We kindly request your response at your earliest convenience.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'hiring',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  hiring_rejection: {
    subject: 'Architecture BRIO — Application Update',
    body: [
      'Dear {candidateName},',
      '',
      'Thank you for your interest in the {jobRole} position at Architecture BRIO and for taking the time to go through our selection process.',
      '',
      'After careful consideration, we have decided not to proceed with your application at this time. This was not an easy decision, as we received many strong applications.',
      '',
      'We wish you all the best in your future endeavours and thank you again for your interest in Architecture BRIO.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'hiring',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  hiring_future_potential: {
    subject: 'Architecture BRIO — Keeping in Touch',
    body: [
      'Dear {candidateName},',
      '',
      'Thank you for your interest in the {jobRole} position at Architecture BRIO.',
      '',
      'While we do not have a suitable opening at the moment, we were impressed by your profile and would like to keep your information on file for future opportunities.',
      '',
      'We will reach out if a position matching your skills and experience becomes available. In the meantime, please feel free to stay in touch.',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'hiring',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  hiring_contact: {
    subject: 'Architecture BRIO — {jobRole} Position',
    body: [
      'Dear {candidateName},',
      '',
      '',
      '',
      'Kind regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'hiring',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  // ── Client Wishes Templates ─────────────────────────────────────────────
  client_birthday: {
    subject: 'Happy Birthday from Architecture BRIO!',
    body: [
      'Dear {clientName},',
      '',
      'On behalf of the entire team at Architecture BRIO, we would like to wish you a very happy birthday!',
      '',
      'We truly value our partnership and the opportunity to work together. May this special day bring you joy and wonderful moments.',
      '',
      'Warm regards,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'client_wishes',
    isFinance: false,
    requiresInvoicePdf: false,
  },
  client_anniversary: {
    subject: 'Happy Anniversary — Architecture BRIO',
    body: [
      'Dear {clientName},',
      '',
      'We are delighted to celebrate this milestone with you! On behalf of the team at Architecture BRIO, we extend our warmest congratulations on your anniversary.',
      '',
      'It has been a privilege to be part of your journey, and we look forward to continuing our collaboration in the years ahead.',
      '',
      'With best wishes,',
      'Architecture BRIO',
    ].join('\n'),
    category: 'client_wishes',
    isFinance: false,
    requiresInvoicePdf: false,
  },
};

/**
 * Returns the built-in default template for a given event type.
 * Used by the Email Templates tab to show defaults when no DB row exists.
 * @param {string} eventType - Template key (e.g. 'stage_initiated')
 * @returns {{ subject: string, body: string } | null}
 */
export function getDefaultTemplate(eventType) {
  const t = TEMPLATES[eventType];
  if (!t) return null;
  return { subject: t.subject, body: t.body };
}

/**
 * Resolve the best template for an event type from custom templates.
 * Resolution order:
 *   1. Stage-specific template (if stageKey matches)
 *   2. Default template for this type (is_default = true)
 *   3. First custom template for this type
 *   4. Built-in template
 *
 * @param {string} eventType - Template key (e.g. 'stage_initiated')
 * @param {Array} customTemplates - Array of template rows from useSupabaseEmailTemplates
 * @param {string} [stageKey] - Optional stage key for stage-specific lookup
 * @returns {{ subject: string, body: string, templateId?: number, templateName?: string }}
 */
export function resolveTemplate(eventType, customTemplates, stageKey) {
  if (customTemplates && customTemplates.length > 0) {
    const ofType = customTemplates.filter(t => t.templateType === eventType);

    // 1. Stage-specific match
    if (stageKey) {
      const stageMatch = ofType.find(t => t.stageKey === stageKey);
      if (stageMatch && (stageMatch.subject || stageMatch.body)) {
        return { subject: stageMatch.subject, body: stageMatch.body, templateId: stageMatch.id, templateName: stageMatch.templateName };
      }
    }

    // 2. Default for this type
    const defaultTpl = ofType.find(t => t.isDefault);
    if (defaultTpl && (defaultTpl.subject || defaultTpl.body)) {
      return { subject: defaultTpl.subject, body: defaultTpl.body, templateId: defaultTpl.id, templateName: defaultTpl.templateName };
    }

    // 3. First with content
    const first = ofType.find(t => t.subject || t.body);
    if (first) {
      return { subject: first.subject, body: first.body, templateId: first.id, templateName: first.templateName };
    }
  }

  // 4. Built-in fallback
  const builtin = TEMPLATES[eventType];
  if (builtin) {
    return { subject: builtin.subject, body: builtin.body };
  }

  return { subject: '', body: '' };
}

// ── Placeholder validation ───────────────────────────────────────────────────

const ALLOWED_PLACEHOLDERS = new Set(ALL_PLACEHOLDERS);

/**
 * Required placeholders per template type.
 * Missing required placeholders produce a warning (overridable), not a hard error.
 */
const REQUIRED_PLACEHOLDERS = {
  project_initiated: ['stageName', 'projectName'],
  stage_initiated:   ['stageName', 'projectName'],
  stage_completed:   ['stageName', 'projectName'],
  stage_approved:    ['stageName'],
  stage_closure:     ['stageName'],
  planned_date_revised: ['stageName', 'projectCode'],
  stage_completion_approval: ['stageName', 'projectCode'],
  payment_confirmed: ['stageName', 'projectCode'],
  proforma_invoice_admin: ['stageName', 'projectCode'],
  tax_invoice_admin: ['stageName', 'projectCode'],
  invoice_sent:      ['invoiceNumber', 'paymentTermsDays'],
  reminder_friendly: ['invoiceNumber', 'dueDate'],
  reminder_due_today:['invoiceNumber', 'dueDate'],
  reminder_overdue:  ['invoiceNumber', 'dueDate'],
  reminder_final:    ['invoiceNumber', 'dueDate'],
};

/**
 * Extract all {placeholder} tokens from a template string.
 * @param {string} text
 * @returns {string[]} Unique placeholder names (without braces)
 */
export function extractPlaceholders(text) {
  if (!text) return [];
  const matches = text.match(/\{(\w+)\}/g);
  if (!matches) return [];
  return [...new Set(matches.map(m => m.slice(1, -1)))];
}

/**
 * Validate placeholders in a template.
 *  - errors:   unknown placeholders (should block save)
 *  - warnings: missing required placeholders (allow override)
 *
 * @param {string} templateType
 * @param {string} subject
 * @param {string} body
 * @returns {{ errors: string[], warnings: string[] }}
 */
export function validateTemplatePlaceholders(templateType, subject, body) {
  const errors = [];
  const warnings = [];
  const allText = `${subject || ''}\n${body || ''}`;
  const found = extractPlaceholders(allText);

  found.forEach(p => {
    if (!ALLOWED_PLACEHOLDERS.has(p)) {
      errors.push(`Unknown placeholder: {${p}}. Please use only supported fields.`);
    }
  });

  const required = REQUIRED_PLACEHOLDERS[templateType] || [];
  required.forEach(p => {
    if (!found.includes(p)) {
      warnings.push(`This template does not contain {${p}}. Continue anyway?`);
    }
  });

  return { errors, warnings };
}

/**
 * Validate an email draft before opening the mail client.
 * Checks recipients, subject, body length, unresolved placeholders, and PDF.
 *
 * @param {object} params
 * @param {string[]} params.recipients
 * @param {string}   params.subject  - Rendered subject
 * @param {string}   params.body     - Rendered body
 * @param {boolean}  [params.requiresInvoicePdf]
 * @param {boolean}  [params.hasPdfAttachment]
 * @returns {string[]} Validation issues (empty = valid)
 */
export function validateEmailBeforeSend({ recipients, subject, body, requiresInvoicePdf: needsPdf, hasPdfAttachment }) {
  const issues = [];
  if (!recipients || recipients.length === 0) issues.push('At least one recipient is required.');
  if (!subject || !subject.trim()) issues.push('Subject line is empty.');
  if (!body || body.trim().length < 20) issues.push('Email body is too short (minimum 20 characters).');

  const unresolved = extractPlaceholders(`${subject || ''}\n${body || ''}`);
  if (unresolved.length > 0) {
    issues.push(`Unresolved placeholders: ${unresolved.map(p => `{${p}}`).join(', ')}`);
  }
  if (needsPdf && !hasPdfAttachment) issues.push('Invoice PDF must be generated before sending.');
  return issues;
}

// ── Map timeline field names to event types ─────────────────────────────────

const FIELD_TO_EVENT_TYPE = {
  contractDate: 'stage_initiated',
  actualDate: 'stage_completion_approval',
  clientApprovalDate: 'stage_approved',
  proformaInvoiceDate: 'proforma_invoice_admin',
  invoiceDate: 'tax_invoice_admin',
};

// ── Map event types to contact flag keys ────────────────────────────────────

const EVENT_TO_CONTACT_FLAG = {
  stage_initiated: 'useForStageUpdates',
  stage_completed: 'useForStageUpdates',
  stage_completion_approval: 'useForStageUpdates',
  stage_approved: 'useForApprovals',
  planned_date_revised: 'useForStageUpdates',
  payment_confirmed: 'useForStageUpdates',
  invoice_sent: 'useForInvoicing',
  proforma_invoice_admin: 'useForInvoicing',
  tax_invoice_admin: 'useForInvoicing',
};

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns the event type string for a given timeline field name.
 * Returns null if the field doesn't trigger an email.
 */
export function getEventTypeForField(fieldName) {
  return FIELD_TO_EVENT_TYPE[fieldName] || null;
}

/**
 * Replace {placeholders} in a string with values from vars.
 * Unreplaced placeholders remain as {key} for visibility.
 */
function replacePlaceholders(str, vars) {
  return str.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`);
}

/**
 * Render a template by replacing {placeholders} with values.
 * Supports addressee, intro/closing messages, and all dynamic placeholders.
 *
 * @param {string} eventType - Template key (e.g. 'stage_initiated', 'invoice_sent')
 * @param {object} vars - All placeholder values including {addressee}
 * @param {object} [customTemplates] - Admin-editable templates from app_settings
 * @param {object} [options] - Additional options
 * @param {string} [options.introMessage] - Inserted after greeting, before template body
 * @param {string} [options.closingMessage] - Inserted after template body, before sign-off
 * @returns {{ subject: string, body: string, requiresInvoicePdf: boolean }}
 */
export function renderEmailTemplate(eventType, vars = {}, customTemplates, options = {}) {
  const template = (customTemplates && customTemplates[eventType]) || TEMPLATES[eventType];
  if (!template) {
    return { subject: '', body: '', requiresInvoicePdf: false };
  }

  // Build the full body with optional intro/closing sections
  let bodyTemplate = template.body;

  if (options.introMessage || options.closingMessage) {
    // Split the template around the greeting and sign-off
    const lines = bodyTemplate.split('\n');
    const greetingIdx = lines.findIndex(l => l.startsWith('Dear '));
    const signOffIdx = lines.findIndex(l => l.startsWith('Kind regards,'));
    const result = [];

    // Greeting line
    if (greetingIdx >= 0) {
      result.push(lines[greetingIdx], '');
    }

    // Intro message (after greeting)
    if (options.introMessage) {
      result.push(options.introMessage.trim(), '');
    }

    // Core body (between greeting and sign-off, excluding empty lines around edges)
    const bodyStart = greetingIdx >= 0 ? greetingIdx + 1 : 0;
    const bodyEnd = signOffIdx >= 0 ? signOffIdx : lines.length;
    const coreLines = lines.slice(bodyStart, bodyEnd);
    // Trim leading/trailing empty lines from core
    while (coreLines.length > 0 && coreLines[0].trim() === '') coreLines.shift();
    while (coreLines.length > 0 && coreLines[coreLines.length - 1].trim() === '') coreLines.pop();
    result.push(...coreLines, '');

    // Closing message (before sign-off)
    if (options.closingMessage) {
      result.push(options.closingMessage.trim(), '');
    }

    // Sign-off
    if (signOffIdx >= 0) {
      result.push(...lines.slice(signOffIdx));
    }

    bodyTemplate = result.join('\n');
  }

  return {
    subject: replacePlaceholders(template.subject, vars),
    body: replacePlaceholders(bodyTemplate, vars),
    requiresInvoicePdf: template.requiresInvoicePdf || false,
  };
}

/**
 * Resolve which client contacts should be preselected for a given event type.
 *
 * Resolution order:
 * 0. If project has contactPreferences with explicit email list for this event type, use that
 * 1. Contacts flagged for this event type (e.g. useForInvoicing)
 * 2. Primary contact(s) if no flagged contacts
 * 3. Legacy client.primaryEmail / client.email as fallback
 *
 * @param {object} client - Client record (from useSupabaseClients)
 * @param {string} eventType - 'stage_initiated' | 'stage_approved' | 'invoice_sent'
 * @param {object} [projectContactPrefs] - Optional project.contactPreferences override
 * @returns {Array<{ name: string, email: string }>}
 */
export function getClientContactsForEvent(client, eventType, projectContactPrefs) {
  if (!client) return [];

  const flagKey = EVENT_TO_CONTACT_FLAG[eventType];
  const contacts = Array.isArray(client.additionalContacts) ? client.additionalContacts : [];

  // Build a unified list: primary contact + additional contacts
  const allContacts = [];

  // Primary contact (from top-level client fields)
  const primaryEmail = client.primaryEmail || client.email || '';
  if (primaryEmail) {
    allContacts.push({
      name: client.name || '',
      email: primaryEmail,
      isPrimary: true,
      useForInvoicing: client.useForInvoicing ?? true, // primary defaults to true
      useForApprovals: client.useForApprovals ?? true,
      useForStageUpdates: client.useForStageUpdates ?? true,
    });
  }

  // Additional contacts
  contacts.forEach(c => {
    if (c.primaryEmail || c.email) {
      allContacts.push({
        name: c.name || '',
        email: c.primaryEmail || c.email || '',
        isPrimary: c.isPrimary || false,
        useForInvoicing: c.useForInvoicing || false,
        useForApprovals: c.useForApprovals || false,
        useForStageUpdates: c.useForStageUpdates || false,
      });
    }
  });

  if (allContacts.length === 0) return [];

  // 0. Project-level override: contactPreferences.{eventType} = [email1, email2, ...]
  if (projectContactPrefs && projectContactPrefs[eventType]) {
    const preferredEmails = projectContactPrefs[eventType];
    if (Array.isArray(preferredEmails) && preferredEmails.length > 0) {
      const emailSet = new Set(preferredEmails.map(e => e.toLowerCase()));
      const matched = allContacts
        .filter(c => emailSet.has(c.email.toLowerCase()))
        .map(({ name, email }) => ({ name, email }));
      if (matched.length > 0) return matched;
      // If preferred emails don't match any current contacts, fall through
    }
  }

  // 1. Try flagged contacts
  if (flagKey) {
    const flagged = allContacts.filter(c => c[flagKey]);
    if (flagged.length > 0) return flagged.map(({ name, email }) => ({ name, email }));
  }

  // 2. Fall back to primary
  const primaries = allContacts.filter(c => c.isPrimary);
  if (primaries.length > 0) return primaries.map(({ name, email }) => ({ name, email }));

  // 3. Fall back to first available
  return [{ name: allContacts[0].name, email: allContacts[0].email }];
}

/**
 * Get all contacts with email for a client (for the recipient picker).
 */
export function getAllClientContacts(client) {
  if (!client) return [];

  const result = [];
  const primaryEmail = client.primaryEmail || client.email || '';
  if (primaryEmail) {
    result.push({
      name: client.name || '',
      email: primaryEmail,
      isPrimary: true,
      roleLabel: 'Primary',
    });
  }

  const contacts = Array.isArray(client.additionalContacts) ? client.additionalContacts : [];
  contacts.forEach(c => {
    const email = c.primaryEmail || c.email || '';
    if (email) {
      result.push({
        name: c.name || '',
        email,
        isPrimary: c.isPrimary || false,
        roleLabel: c.relationship || c.company || '',
      });
    }
  });

  return result;
}

/**
 * Basic email validation.
 */
export function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Build a mailto URL from recipients, subject, and body.
 * Properly encodes all parts for safe URL generation.
 *
 * @param {{ to: string[], cc?: string[], subject: string, body: string }} params
 * @returns {string} mailto URL
 */
export function buildMailtoUrl({ to = [], cc = [], bcc = [], subject = '', body = '' }) {
  if (to.length === 0) return '';

  // mailto: spec — addresses in the path are NOT percent-encoded,
  // but query parameters (subject, body, cc, bcc) ARE encoded.
  const toStr = to.map(e => e.trim()).join(',');
  const params = [];
  if (cc.length > 0) params.push(`cc=${encodeURIComponent(cc.join(','))}`);
  if (bcc.length > 0) params.push(`bcc=${encodeURIComponent(bcc.join(','))}`);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);

  return `mailto:${toStr}${params.length > 0 ? '?' + params.join('&') : ''}`;
}

/**
 * Format a date string (YYYY-MM-DD) to DD/MM/YYYY for display in emails.
 */
export function formatDateForEmail(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * Calculate payment due date from invoice date and client payment terms.
 * @param {string} invoiceDate - YYYY-MM-DD
 * @param {number|null} paymentTermsDays - Number of days (falls back to 14)
 * @returns {{ dueDate: string, usingFallback: boolean }}
 */
export function calculateDueDate(invoiceDate, paymentTermsDays) {
  if (!invoiceDate) return { dueDate: '', usingFallback: false };
  const days = paymentTermsDays != null ? Number(paymentTermsDays) : 14;
  const usingFallback = paymentTermsDays == null;
  const d = new Date(invoiceDate);
  d.setDate(d.getDate() + days);
  const dueDate = d.toISOString().slice(0, 10);
  return { dueDate, usingFallback };
}

/**
 * Compute the display status of an invoice factoring in due date.
 * @param {object} inv - Invoice record
 * @param {string} dueDate - YYYY-MM-DD
 * @returns {'draft'|'sent'|'due'|'overdue'|'paid'}
 */
export function getInvoiceDisplayStatus(inv, dueDate) {
  if (!inv) return 'draft';
  if (inv.status === 'paid' || inv.paidConfirmed) return 'paid';
  if (inv.status === 'draft') return 'draft';
  // inv.status === 'issued' → check due date
  if (!dueDate) return 'sent';
  const today = new Date().toISOString().slice(0, 10);
  if (today > dueDate) return 'overdue';
  if (today === dueDate) return 'due';
  return 'sent';
}

/**
 * Returns whether a given event type is finance-related (should BCC admin).
 * @param {string} eventType
 * @returns {boolean}
 */
export function isFinanceEvent(eventType) {
  const tmpl = TEMPLATES[eventType];
  return tmpl?.isFinance === true;
}

/**
 * Get the admin BCC email for finance communications.
 */
export function getAdminBccEmail() {
  return ADMIN_EMAIL;
}

/**
 * Get the list of available reminder template types.
 */
export function getReminderTemplateTypes() {
  return [
    { key: 'reminder_friendly', label: 'Friendly Reminder' },
    { key: 'reminder_due_today', label: 'Due Today' },
    { key: 'reminder_overdue', label: 'Overdue Reminder' },
    { key: 'reminder_final', label: 'Final Reminder' },
  ];
}

/**
 * Get the list of available hiring template types.
 */
export function getHiringTemplateTypes() {
  return Object.entries(TEMPLATES)
    .filter(([, t]) => t.category === 'hiring')
    .map(([key, t]) => ({ key, label: t.subject.replace(/\{[^}]+\}/g, '…') }));
}

/**
 * Get all template categories with their templates.
 * @returns {Array<{ key: string, label: string, isFinance: boolean, templates: Array<{ key: string, label: string }> }>}
 */
export function getTemplateCategories() {
  return TEMPLATE_CATEGORIES.map(cat => ({
    ...cat,
    templates: Object.entries(TEMPLATES)
      .filter(([, t]) => t.category === cat.key)
      .map(([key, t]) => ({ key, label: t.subject.replace(/\{[^}]+\}/g, '…') })),
  }));
}

/**
 * Get a raw template definition (for editing in the UI).
 * @param {string} eventType
 * @returns {{ subject: string, body: string, category: string, isFinance: boolean, requiresInvoicePdf: boolean } | null}
 */
export function getTemplateDefinition(eventType) {
  return TEMPLATES[eventType] || null;
}

/**
 * Get the list of all supported placeholder keys.
 */
export function getAvailablePlaceholders() {
  return [...ALL_PLACEHOLDERS];
}

/**
 * Check if a template requires an invoice PDF attachment.
 * @param {string} eventType
 * @returns {boolean}
 */
export function requiresInvoicePdf(eventType) {
  return TEMPLATES[eventType]?.requiresInvoicePdf === true;
}

/**
 * Format a timeline block for inclusion in email body.
 * Renders stage dates in a clean structured format.
 *
 * @param {object} stageTimeline - project.stageTimeline object
 * @param {Array<{ key: string, name: string }>} stages - Ordered stage list
 * @returns {string} Formatted timeline text block
 */
export function formatTimelineBlock(stageTimeline, stages) {
  if (!stageTimeline || !stages || stages.length === 0) return '';

  const lines = ['Project Timeline:', '─'.repeat(40)];

  stages.forEach(stage => {
    const tl = stageTimeline[stage.key];
    if (!tl) return;

    const dates = [];
    if (tl.contractDate) dates.push(`Initiated: ${formatDateForEmail(tl.contractDate)}`);
    if (tl.clientApprovalDate) dates.push(`Approved: ${formatDateForEmail(tl.clientApprovalDate)}`);
    if (tl.proformaInvoiceDate) dates.push(`Proforma: ${formatDateForEmail(tl.proformaInvoiceDate)}`);
    if (tl.invoiceDate) dates.push(`Invoiced: ${formatDateForEmail(tl.invoiceDate)}`);

    if (dates.length > 0) {
      lines.push(`  ${expandStageName(stage.name)}`);
      dates.forEach(d => lines.push(`    ${d}`));
    }
  });

  lines.push('─'.repeat(40));
  return lines.join('\n');
}

/**
 * Format a team member list for email inclusion.
 * @param {Array<{ name: string, role?: string }>} members
 * @returns {string}
 */
export function formatTeamList(members) {
  if (!members || members.length === 0) return '(No team members assigned)';
  return members
    .map(m => m.role ? `  • ${m.name} — ${m.role}` : `  • ${m.name}`)
    .join('\n');
}

/**
 * Build the full set of template variables from project/client/invoice context.
 * Auto-populates all supported placeholders.
 *
 * @param {object} params
 * @param {object} [params.client]
 * @param {object} [params.project]
 * @param {string} [params.stageName]
 * @param {string} [params.nextStage]
 * @param {string} [params.previousStage]
 * @param {string} [params.dateValue] - YYYY-MM-DD
 * @param {object} [params.invoice]
 * @param {string} [params.addressee] - Explicit addressee override
 * @param {Array<{ key: string, name: string }>} [params.stages] - For timeline block
 * @param {Array<{ name: string, role?: string }>} [params.teamMembers] - For team list
 * @param {string} [params.meetingDate] - YYYY-MM-DD
 * @returns {object} vars object ready for renderEmailTemplate
 */
export function buildTemplateVars(params = {}) {
  const { client, project, invoice, stages } = params;

  // Auto-derive addressee: explicit > primary contact name > client org name
  const addressee = params.addressee
    || client?.name
    || client?.organisation
    || '';

  // Auto-derive next/previous stage if stages array provided
  let nextStage = expandStageName(params.nextStage) || '';
  let previousStage = expandStageName(params.previousStage) || '';
  if (stages && params.stageName && (!nextStage || !previousStage)) {
    const idx = stages.findIndex(s => s.name === params.stageName);
    if (idx >= 0) {
      if (!nextStage && idx < stages.length - 1) nextStage = expandStageName(stages[idx + 1].name);
      if (!previousStage && idx > 0) previousStage = expandStageName(stages[idx - 1].name);
    }
  }

  // Timeline block
  const timeline = (project?.stageTimeline && stages)
    ? formatTimelineBlock(project.stageTimeline, stages)
    : '';

  // Team list
  const teamList = params.teamMembers
    ? formatTeamList(params.teamMembers)
    : '';

  return {
    addressee,
    clientName: client?.organisation || client?.name || '',
    projectName: project?.name || '',
    projectCode: project?.code || project?.projectCode || '',
    stageName: expandStageName(params.stageName) || '',
    nextStage,
    previousStage,
    invoiceNumber: invoice?.invoiceNumber || '',
    invoiceDate: formatDateForEmail(invoice?.invoiceDate || params.dateValue || ''),
    dueDate: formatDateForEmail(params.dueDate || ''),
    paymentTermsDays: params.paymentTermsDays != null ? String(params.paymentTermsDays) : '',
    amount: params.amount || '',
    meetingDate: formatDateForEmail(params.meetingDate || ''),
    teamList,
    timeline,
    date: formatDateForEmail(params.dateValue || ''),
    previousDate: formatDateForEmail(params.previousDate || params.extraVars?.previousDate || ''),
  };
}

/**
 * Validate email draft before opening mail client.
 * Returns array of validation errors (empty = valid).
 *
 * @param {object} params
 * @param {Array<{ email: string }>} params.recipients
 * @param {string} params.addressee
 * @param {string} params.eventType
 * @param {boolean} [params.hasPdfAttachment] - Whether PDF is available
 * @returns {Array<{ field: string, message: string }>}
 */
export function validateEmailDraft({ recipients, addressee, eventType, hasPdfAttachment }) {
  const errors = [];

  if (!recipients || recipients.length === 0) {
    errors.push({ field: 'recipients', message: 'At least one recipient is required' });
  }

  if (!addressee || !addressee.trim()) {
    errors.push({ field: 'addressee', message: 'Addressee name is required for the greeting' });
  }

  if (requiresInvoicePdf(eventType) && !hasPdfAttachment) {
    errors.push({ field: 'attachment', message: 'Invoice PDF must be generated before sending this email type' });
  }

  return errors;
}
